import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import logger from '@/utils/logger';
import { SMS_DISCLOSURE_VERSION } from '@/utils/smsConsent';
import { SmsConsentCheckbox } from '@/components/SmsConsentCheckbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  'Electrical Troubleshooting & Repairs', 'Circuit Breaker Panel Replacement',
  'EV Charging Installation', 'Generator & Backup Power',
  'Lighting & Switch Installation', 'Pool/Spa Wiring', 'Other',
];

const EMPTY_FORM = { name: '', email: '', phone: '', address: '', service: '', date_of_visit: '', time_of_visit: '', message: '', sms_consent: false };

// Animation constants - stable references
const fadeIn = { initial: { opacity: 0 }, whileInView: { opacity: 1 }, viewport: { once: true } };
const fadeInUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };
const halfSec = { duration: 0.5 };
const delayedHalfSec = { duration: 0.5, delay: 0.1 };
const scaleIn = { initial: { opacity: 0, scale: 0.95 }, animate: { opacity: 1, scale: 1 } };
const inputCls = "w-full border border-black px-4 py-3 font-body text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-0 bg-white";

function useContactForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState('idle');

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) return;
    setStatus('loading');
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        source: 'homepage-request-consultation',
        sms_consent_source: 'homepage-request-consultation',
        sms_consent_page_url: window.location.href,
        sms_disclosure_version: SMS_DISCLOSURE_VERSION,
      });
      setStatus('success');
      setForm(EMPTY_FORM);
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          send_to: 'AW-11468865441/cKNeCNLetJwcEKH_49wq',
          value: 1.0,
          currency: 'USD',
        });
        window.gtag('event', 'conversion', {
          send_to: 'AW-11468865441/A1FMCNivoqIcEKH_49wq',
        });
        window.gtag('event', 'conversion', {
          send_to: 'AW-11468865441/_u8lCMnP8KscEKH_49wq',
        });
        window.gtag('event', 'generate_lead', {
          event_category: 'Contact',
          event_label: form.service || 'General Inquiry',
        });
      }
    } catch (err) {
      logger.error('Lead submission failed:', err);
      setStatus('error');
    }
  }, [form]);

  const reset = useCallback(() => setStatus('idle'), []);

  return { form, status, handleChange, handleSubmit, reset };
}

function ContactInfo() {
  return (
    <div className="lg:col-span-4">
      <motion.p {...fadeIn} className="font-body text-xs uppercase tracking-[0.2em] text-zinc-400 mb-3">Request Service</motion.p>
      <motion.h2 {...fadeInUp} transition={halfSec} className="font-heading text-3xl sm:text-4xl font-black tracking-tight uppercase mb-4">Request a Consultation</motion.h2>
      <motion.p {...fadeInUp} transition={delayedHalfSec} className="font-body text-sm text-zinc-500 leading-relaxed mb-6">
        Fill out the form and we&apos;ll get back to you within 24 hours. For emergencies, call us directly.
      </motion.p>
      <div className="space-y-3">
        <p className="font-body text-sm text-zinc-600"><span className="font-semibold">Phone:</span> (615) 961-5930</p>
        <p className="font-body text-sm text-zinc-600"><span className="font-semibold">Hours:</span> Mon–Fri 8:00 AM – 4:30 PM | Weekends by appointment</p>
        <p className="font-body text-sm text-zinc-600"><span className="font-semibold">Area:</span> Nashville & Surrounding Counties</p>
      </div>
    </div>
  );
}

function ContactSuccess({ onReset }) {
  return (
    <motion.div {...scaleIn} className="border border-black bg-white p-12 text-center" data-testid="contact-success">
      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
      <h3 className="font-heading text-xl font-bold uppercase mb-2">Request Submitted!</h3>
      <p className="font-body text-sm text-zinc-500">We&apos;ll contact you within 24 hours. For emergencies, call (615) 961-5930.</p>
      <button onClick={onReset} data-testid="contact-reset-btn"
        className="mt-6 bg-yellow-400 text-black font-heading font-bold text-sm uppercase px-6 py-3 border border-black hover:-translate-y-0.5 transition-transform">
        Submit Another Request
      </button>
    </motion.div>
  );
}

function FormField({ label, name, type = 'text', required, placeholder, value, onChange, as, children }) {
  const Tag = as || 'input';
  return (
    <div>
      <label className="font-body text-xs uppercase tracking-wider text-zinc-500 mb-1 block">{label}</label>
      {children || (
        <Tag type={type} name={name} value={value} onChange={onChange} required={required}
          data-testid={`contact-${name}-input`} className={inputCls} placeholder={placeholder}
          {...(Tag === 'textarea' ? { rows: 4 } : {})} />
      )}
    </div>
  );
}

function ContactForm({ form, status, onChange, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="border border-black bg-white p-8" data-testid="contact-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <FormField label="Name *" name="name" value={form.name} onChange={onChange} required placeholder="Your full name" />
        <FormField label="Email *" name="email" type="email" value={form.email} onChange={onChange} required placeholder="your@email.com" />
        <FormField label="Phone *" name="phone" type="tel" value={form.phone} onChange={onChange} required placeholder="(615) 000-0000" />
        <FormField label="Address" name="address">
          <AddressAutocomplete name="address" value={form.address} onChange={onChange} placeholder="Start typing your address..." className={inputCls} testId="contact-address-input" />
        </FormField>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <FormField label="Service" name="service">
          <select name="service" value={form.service} onChange={onChange} data-testid="contact-service-select" className={inputCls}>
            <option value="">Select a service</option>
            {serviceOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Preferred Date" name="date_of_visit" type="date" value={form.date_of_visit} onChange={onChange} />
        <FormField label="Preferred Time" name="time_of_visit" type="time" value={form.time_of_visit} onChange={onChange} />
      </div>
      <div className="mb-6">
        <FormField label="Message" name="message" as="textarea" value={form.message} onChange={onChange} placeholder="Describe your electrical needs..." />
      </div>
      <SmsConsentCheckbox checked={form.sms_consent} onChange={onChange} testId="contact-sms-consent" />
      {status === 'error' && (
        <p className="font-body text-sm text-red-500 mb-4" data-testid="contact-error">Something went wrong. Please try again or call us directly.</p>
      )}
      <button type="submit" disabled={status === 'loading'} data-testid="contact-submit-btn"
        className="inline-flex items-center gap-2 bg-yellow-400 text-black font-heading font-bold text-sm uppercase tracking-wider px-8 py-4 border border-black hover:-translate-y-1 transition-transform disabled:opacity-50 disabled:cursor-not-allowed">
        {status === 'loading' ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <><Send className="w-4 h-4" /> Submit Request</>}
      </button>
    </form>
  );
}

import AgenticLeadForm from '@/components/AgenticLeadForm';

export default function ContactSection() {
  const { form, status, handleChange, handleSubmit, reset } = useContactForm();

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 px-6 bg-zinc-50 border-t border-zinc-200">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <ContactInfo />
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-8">
              <div className="xl:col-span-3">
                {status === 'success'
                  ? <ContactSuccess onReset={reset} />
                  : <ContactForm form={form} status={status} onChange={handleChange} onSubmit={handleSubmit} />}
              </div>
              <div className="xl:col-span-2 flex items-start justify-center">
                <AgenticLeadForm />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
